import React from "react";
import { Box, Grid, Typography, makeStyles, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import AppDialog from "components/Dialog/AppDialog";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";

const useStyles = makeStyles(() => ({
  ruleSection: {
    marginBottom: "1rem",
    padding: "12px",
    backgroundColor: "#F9FAFB",
    borderRadius: "8px",
    border: "1px solid #E5E7EB",
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: AppColor.mainColor,
    textTransform: "uppercase",
    marginBottom: "8px",
  },
  fieldRow: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "12px",
    "&:last-child": {
      marginBottom: 0,
    },
  },
  fieldName: {
    fontSize: 13,
    fontWeight: 600,
    color: "#111827",
  },
  fieldDetails: {
    fontSize: 13,
    color: "#4B5563",
    marginLeft: "12px",
  },
  tagRequired: {
    display: "inline-block",
    padding: "2px 6px",
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: "#FEE2E2",
    color: "#991B1B",
    borderRadius: "4px",
    marginLeft: "8px",
  },
  tagOptional: {
    display: "inline-block",
    padding: "2px 6px",
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: "#E5E7EB",
    color: "#374151",
    borderRadius: "4px",
    marginLeft: "8px",
  },
  bulletPoint: {
    position: "relative",
    paddingLeft: "12px",
    "&::before": {
      content: '"•"',
      position: "absolute",
      left: 0,
      color: "#9CA3AF",
    },
  },
  tableContainer: {
    maxHeight: 500,
    marginTop: 16,
    border: "1px solid #E5E7EB",
    borderRadius: 8,
  },
  tableCellHead: {
    fontSize: 13,
    fontWeight: 700,
    color: "#374151",
    textTransform: "uppercase",
    borderBottom: "1px solid #E5E7EB",
    padding: "12px 16px",
    backgroundColor: "#F9FAFB",
  },
  tableCellBody: {
    fontSize: 13,
    color: "#4B5563",
    borderBottom: "1px solid #E5E7EB",
    padding: "12px 16px",
    verticalAlign: "top",
  },
}));

const getRequiredFields = (t) => [
  { name: "label.role", details: [
    <div>
      {t("importRules.values")}:
      <div style={{ marginLeft: 12 }}>
        <div>{t('user_role.NURSE')}: NURSE</div>
        <div>{t('user_role.SUPPORT_WORKER')}: SUPPORT_WORKER</div>
        <div>{t('user_role.SENIOR_SUPPORT_WORKER')}: SENIOR_SUPPORT_WORKER</div>
        <div>{t('user_role.TEAM_LEADER')}: TEAM_LEADER</div>
        <div>{t('user_role.HOUSE_COORDINATOR')}: HOUSE_COORDINATOR</div>
        <div>{t('user_role.REGISTERED_NURSE')}: REGISTERED_NURSE</div>
        <div>{t('user_role.CLINICAL_NURSE_MANAGER')}: CLINICAL_NURSE_MANAGER</div>
        <div>{t('user_role.OPERATIONS_MANAGER')}: OPERATIONS_MANAGER</div>
        <div>{t('user_role.HR_MANAGER')}: HR_MANAGER</div>
        <div>{t('user_role.DIRECTOR')}: DIRECTOR</div>
      </div>
    </div>
  ] },
  { name: "label.email", details: [
    "The @ symbol must be in the middle (not at the beginning).",
    "The beginning (before the @ symbol) can only contain letters, numbers, or symbols like . _ % + -.",
    "The end (after the @ symbol) must contain a period (.) and end with a letter (e.g., .com, .vn, .org).",
    "Spaces or other characters besides commas are not allowed."
  ] },
  { name: "label.first_name", details: [t('importRules.max_length', {value: 35})] },
  { name: "label.last_name", details: [t('importRules.max_length', {value: 35})] },
  { name: "label.phone", details: [t('importRules.max_length', {value: 10}), t('importRules.only_number')] },
  { name: "label.employmentType", details: [
    <div>
      {t("importRules.values")}:
      <div style={{ marginLeft: 12 }}>
        <div>{t('label.employmentTypePartTime')}: PARTTIME</div>
        <div>{t('label.employmentTypeFullTime')}: FULLTIME</div>
        <div>{t('label.employmentTypeCasual')}: CASUAL</div>
      </div>
    </div>
  ] },
];

const getOptionalFields = (t) => [
  { name: "label.dateOfBirth", details: [t('importRules.format', { value: 'yyyy/MM/dd'})] },
  { name: "label.postalCode", details: [t('importRules.max_length', {value: 10}), t('importRules.only_number')] },
  { name: "label.emergencyContactName", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.emergencyContactNumber", details: [t('importRules.only_number')] },
  { name: "label.gender", details: [
    <div>
      {t("importRules.values")}:
      <div style={{ marginLeft: 12 }}>
        <div>{t('label.male')}: MALE</div>
        <div>{t('label.female')}: FEMALE</div>
        <div>{t('label.other')}: OTHER</div>
      </div>
    </div>
  ] },
  { name: "label.citizenshipStatus", details: [
    <div>
      {t("importRules.values")}:
      <div style={{ marginLeft: 12 }}>
        <div>{t('workStatus.AUSTRALIAN_CITIZEN')}: AUSTRALIAN_CITIZEN</div>
        <div>{t('workStatus.NEW_ZEALAND_CITIZEN')}: NEW_ZEALAND_CITIZEN</div>
        <div>{t('workStatus.AUSTRALIA_WORK_VISA')}: AUSTRALIA_WORK_VISA</div>
      </div>
    </div>
  ] },
  { name: "label.startDate", details: [t('importRules.format', { value: 'yyyy/MM/dd'})] },
  { name: "label.bankName", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.accountName", details: [t('importRules.max_length', {value: 32})] },
  { name: "label.bsbNumber", details: [t('importRules.min_length', {value: 6}), t('importRules.max_length', {value: 7}), t('importRules.only_number')] },
  { name: "label.accountNumber", details: [t('importRules.max_length', {value: 9}), t('importRules.only_number')] },
  { name: "label.account#", details: [t('importRules.only_number')] },
  { name: "label.taxNumber", details: [t('importRules.min_length', {value: 8}), t('importRules.max_length', {value: 9}), t('importRules.only_number')] },
  { name: "label.clearanceNumber", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.status", details: [
    <div>
      {t("importRules.values")}:
      <div style={{ marginLeft: 12 }}>
        <div>{t('complianceStatus.CLEARED')}: CLEARED</div>
        <div>{t('complianceStatus.PENDING')}: PENDING</div>
        <div>{t('complianceStatus.EXCLUDED')}: EXCLUDED</div>
        <div>{t('complianceStatus.INTERIM_BAR')}: INTERIM_BAR</div>
        <div>{t('complianceStatus.EXPIRED')}: EXPIRED</div>
        <div>{t('complianceStatus.NOT_SUBMITTED')}: NOT_SUBMITTED</div>
      </div>
    </div>
  ] },
  { name: "label.state", details: [
    <div>
      {t("importRules.values")}:
      <div style={{ marginLeft: 12 }}>
        <div>{t('complianceState.VIC')}: VIC</div>
        <div>{t('complianceState.NSW')}: NSW</div>
        <div>{t('complianceState.QLD')}: QLD</div>
        <div>{t('complianceState.SA')}: SA</div>
        <div>{t('complianceState.WA')}: WA</div>
        <div>{t('complianceState.TAS')}: TAS</div>
        <div>{t('complianceState.ACT')}: ACT</div>
        <div>{t('complianceState.NT')}: NT</div>
      </div>
    </div>
  ] },
  { name: "label.issueDate", details: [t('importRules.format', { value: 'yyyy/MM/dd'})] },
  { name: "label.expiryDate", details: [t('importRules.format', { value: 'yyyy/MM/dd'})] },
  { name: "label.superProvider", details: [] },
  { name: "label.address", details: [] },
];

const ImportWorkerRulesModal = ({ open, onClose }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const requiredFields = getRequiredFields(t);
  const optionalFields = getOptionalFields(t);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={t("importRules.title")}
      maxWidth="md"
    >
      <Box p={2}>
        <Typography variant="body2" style={{ marginBottom: 16, color: "#4B5563" }}>
          {t("importRules.description")}
        </Typography>

        <TableContainer component={Paper} elevation={0} className={classes.tableContainer}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableCellHead} style={{ width: "25%" }}>{t("importRules.fieldName")}</TableCell>
                <TableCell className={classes.tableCellHead} style={{ width: "15%" }}>{t("importRules.required")}</TableCell>
                <TableCell className={classes.tableCellHead} style={{ width: "60%" }}>{t("importRules.formatOrRule")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                ...requiredFields.map(f => ({ ...f, required: true })),
                ...optionalFields.map(f => ({ ...f, required: false }))
              ].map((field, idx) => (
                <TableRow key={idx}>
                  <TableCell className={classes.tableCellBody}>
                    <Box fontWeight={600} color="#111827">
                      {t(field.name)}
                    </Box>
                  </TableCell>
                  <TableCell className={classes.tableCellBody}>
                    {field.required ? (
                      <span className={classes.tagRequired}>{t("importRules.required")}</span>
                    ) : (
                      <span className={classes.tagOptional}>{t("importRules.optional")}</span>
                    )}
                  </TableCell>
                  <TableCell className={classes.tableCellBody}>
                    {field.details.length > 0 ? (
                      field.details.map((detail, dIdx) => (
                        <div key={dIdx} className={classes.bulletPoint}>{detail}</div>
                      ))
                    ) : "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </AppDialog>
  );
};

export default ImportWorkerRulesModal;
